// One agent's chat.
//
// - The header: back, the title, then the type, the status and the schedule (statusLabel, and a
//   watch's end date), the mode, who is on it (the Members dialog), and the "..." menu. The owner's
//   menu: Run now, Try a demo run, Pause or Resume, Edit, Members, Notes, Open in Messages, the email
//   item and Delete, which asks once more. A member's: Members, Notes, Open in Messages, the email item
//   and Leave, which asks once more. Picking an item leaves focus on "...", so a dialog gives it back
//   there on close.
// - A demo run (views.agent_demo) changes and sends nothing: its result is marked Demo, with a note
//   on how to correct it (DEMO_NOTE), and each change or send it would have made is a "Would have"
//   step.
// - A view-as session only reads (every agents endpoint refuses it a change): its menu has only what
//   reads, its cards are locked, its dialogs are read-only, it takes no messages, and nothing is
//   marked seen.
// - A halted agent says so under the header: Resume for the owner of a paused one, "Ask the owner to
//   resume it." for anyone else. An owner whose own access lapsed sees the agent paused and none of its
//   history (history_hidden), notes included, until the access is back.
// - "Needs you": every card still open (pending, being decided, being checked), above the timeline and
//   outside its scroll, so a chat that opens at its newest message never hides them. A card that can't
//   be decided yet is locked with a sentence saying why: its agent is halted, or its run is still
//   working (the server answers both with a 409). It is amber only while a card can be decided now.
// - The edit card sits between "Needs you" and the timeline: the timeline is a live region (a log), so
//   what is put in it is read out.
// - The timeline, oldest first: the instruction and members' messages ("from Messages" on one written
//   in the agent's Messages thread), the agent's results, subagents under their parent's result, and
//   notices. Quiet runs fold into one line. A link's run is marked and scrolled to, and the quiet runs
//   it is folded into open. It says when older messages aren't shown.
// - A run's steps are in the timeline, as Claude Code shows what it is doing. While the run is at work
//   they are at the end (LiveRun): what it is doing as a status ("Working..."; the header says Working
//   or Queued), then every step so far, the one under way turning, and none of them announced. Once
//   it is over they sit above its result (RunSteps), or above its notice when it posted none (a run
//   that failed): look-ups in a row fold into one line that opens, and what it changed, sent or
//   failed, what NotesAI found when it checked an unsure outcome, and what waits for a decision stay
//   in view. Each decided card is one line under the result.
// - Sources: a result's refs (the records it named or its run used, resolved for the viewer by the
//   detail) are chips in its text (AgentMarkdown) and in a Sources row under it (SourcesRow). A press
//   on one opens the chat's one preview of it (SourcePreview), for the result's run. A result from
//   before sources were kept has no refs, and reads as it did.
// - The composer: Enter sends, Shift+Enter starts a new line, and the words stay until the server has
//   taken them. What is sent here also posts to the agent's Messages thread, and the note says so. A
//   halted agent takes no messages. When no answer comes back, the agent is read again, and the words
//   go only if it shows them posted (send).
//
// Reading the agent: on open; each time `version` changes (useAgents: a settled burst of realtime
// events) while it is on screen, and on its return if one came while it was off screen; every 5 s
// while one of its runs is queued or running (runs aren't on realtime), else every 30 s, skipping a
// tick while a read still waits; and at once when it comes back on screen. It polls only while on
// screen. Reads are numbered, so an older answer never overwrites a newer one. Each newer message is
// marked seen, and the list is read again so its badges drop, only while the chat is on screen: the
// tab is visible, and the chat is displayed (the right panel keeps it mounted while collapsed, and so
// does the Chat subtab).
//
// Errors are the server's sentences (agentErrorText). After a 409 or a 403 the agent is read again; a
// 404 means it is gone, or the person is no longer on it. An answer that never came (lostAnswer: no
// answer at all, or a gateway's error page) reads as "Couldn't reach NotesAI just now. Try again.", and
// after a change the agent is read again, so the chat shows where things stand.
//
// Props:
// - agentId; userId (the person; their own entry in the members is the one with email_ok);
// - types: the list's, for the type's name and the edit card;
// - version: useAgents';
// - schema: the schema the person works in (the open chart's), where the members search looks;
// - focusRunId: the run a link points at;
// - draft: a message to start the box with (a question about a form the agent's assistant
//   submitted: AssistedFormBadge), never sent by itself;
// - split: beside the list on a wide screen (the /agents page), so Back shows on narrow ones only;
// - onBack(): leave the chat; onChanged(): something changed, read the list again.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import AgentMarkdown from './AgentMarkdown';
import CardItem, { decisionText, localTime } from './CardItem';
import MembersDialog from './MembersDialog';
import NotesDialog from './NotesDialog';
import PlanCard from './PlanCard';
import SourcePreview from './SourcePreview';
import SourcesRow from './SourcesRow';
import { describeTrigger, statusLabel } from './triggerText';
import { agentErrorText } from './agentErrors';
import * as api from './agentsService';
import { reasonWords, sourceName } from './checkWords';
import { NO_CONNECTION, lostAnswer } from './usePlanRequest';
import { openMessages } from '../../../utils/messagingBus';
import { isImpersonating } from '../../../utils/impersonation';

const ACTIVE_POLL_MS = 5000;
const IDLE_POLL_MS = 30000;
// Scrolled this close to the bottom, the reader is following the newest: new messages keep them there.
const STICK_PX = 40;
// What a message may hold (views.MAX_MESSAGE); the server refuses more with its own sentence.
const MAX_MESSAGE = 4000;

// The characters the words use that aren't ASCII, so this file stays ASCII.
const ELLIPSIS = String.fromCharCode(0x2026);
const DOT = String.fromCharCode(0xb7);
const BACK = String.fromCharCode(0x2190);
const MORE = String.fromCharCode(0x22ef);
const OUT = String.fromCharCode(0x2197);
const TICK = String.fromCharCode(0x2713);
const CROSS = String.fromCharCode(0x2715);
const PAUSE = String.fromCharCode(0x23f8);
const RECHECK = String.fromCharCode(0x27f3);
const WARNING = String.fromCharCode(0x26a0);
const BULLET = String.fromCharCode(0x2022);
const TIMES = String.fromCharCode(0xd7);
const FOLDED = String.fromCharCode(0x25b8);
const UNFOLDED = String.fromCharCode(0x25be);
const ROBOT = String.fromCodePoint(0x1f916);
const PEOPLE = String.fromCodePoint(0x1f465);
const BALLOON = String.fromCodePoint(0x1f4ac);
const WOULD = String.fromCharCode(0x25cb); // a demo's step: what it would have done

// The cards "Needs you" keeps: a claim is published as `deciding` while its request still runs, and a
// card NotesAI is checking stays until the check settles it.
const OPEN = new Set(['pending', 'deciding', 'checking']);
// The statuses in which an agent takes no new run (types.NO_NEW_RUNS): its cards can't be decided.
const HALTED = new Set(['paused', 'suspended', 'archived']);
// The statuses statusLabel names instead of the schedule.
const STOPPED = new Set(['paused', 'done', 'suspended', 'archived']);
const WORKING = new Set(['queued', 'running']);
// The actions whose step sends something out of NotesAI: they go out, rather than go through.
// views.SEND_ACTIONS also has the forms agent's, whose steps only record what NotesAI texts later.
const SEND_ACTIONS = new Set(['send_sms', 'send_email', 'send_fax', 'email_staff']);
// A step's mark, and the word a screen reader says for it.
const STEP_MARKS = new Map([
  ['done', [TICK, 'Done:']],
  ['failed', [CROSS, 'Failed:']],
  ['queued', [PAUSE, 'Waiting for a decision:']],
  ['started', [ELLIPSIS, 'Started:']],
]);
// The agent's own steps that stay in view once its run is over, though they change nothing: what it
// couldn't do (a gap) and the question it asked.
const KEEP_META = new Set(['cannot_do', 'ask_user']);

const HISTORY_HIDDEN = "You can't see this agent's history until your access is back.";
const RUN_WORKING = 'The agent is still working on this. You can decide it once the run has finished.';
const RESUME_TO_DECIDE = 'Resume this agent to decide its cards.';
const ASK_OWNER = 'Ask the owner to resume it.';
// A view-as session reads, and every agents endpoint refuses it a change (403).
const VIEW_AS_CARDS = "You're viewing as someone else, so you can't decide cards.";
const VIEW_AS_MESSAGES = "You're viewing as someone else, so you can't send messages.";
// The detail holds the newest 100 messages (views.MESSAGES_SHOWN); paging back is a follow-up.
const OLDER_HIDDEN = "Older messages aren't shown here.";
// Under a demo run's result (views: the run's `demo`): nothing it describes was done, and how to
// correct it. The agent keeps a correction for its next runs (prompts.FEEDBACK).
export const DEMO_NOTE = 'Demo run: nothing was changed or sent. Tell the agent below what to change, '
  + 'and it will do it that way from now on, or try another demo from the menu.';

const ROOT = 'flex-1 flex flex-col';
const HEADER = 'relative flex items-center gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-800/60';
const ICON_BUTTON = 'px-1 text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200';
const TITLE = 'text-xs font-bold text-slate-900 dark:text-slate-100 truncate';
const SUBTITLE = 'text-[11px] text-slate-500 dark:text-slate-400 truncate';
const PILL = 'shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold';
const MODE_ASK = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300';
const MODE_AUTO = 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300';
const PEOPLE_BUTTON = 'shrink-0 text-[11px] text-slate-500 dark:text-slate-400 hover:underline';
const MENU = 'absolute right-2 top-10 z-20 w-44 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#0f0f16] shadow-lg py-1';
const MENU_ITEM = 'block w-full text-left px-3 py-1.5 text-xs outline-none hover:bg-slate-100 dark:hover:bg-slate-800 focus:bg-slate-100 dark:focus:bg-slate-800';
const PLAIN_ITEM = 'text-slate-700 dark:text-slate-200';
const DANGER_ITEM = 'text-red-600 dark:text-red-400';
const BANNER = 'flex items-center justify-between gap-2 px-3 py-2 border-b border-slate-200 dark:border-slate-800/60 bg-slate-50 dark:bg-slate-900/40 text-[11px] text-slate-600 dark:text-slate-300';
const BUSY = 'px-3 py-1.5 border-b border-slate-200 dark:border-slate-800/60 text-[11px] text-slate-500 dark:text-slate-400';
const RESUME = 'shrink-0 rounded-md px-2.5 py-1 text-[11px] font-semibold bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white';
// "Needs you" gives way first when the pane is short (down to a few lines, then it scrolls); the
// timeline keeps at least 12rem.
const NEEDS = 'shrink min-h-[4.5rem] overflow-y-auto scrollbar-thin px-3 py-2 space-y-2 border-b border-slate-200 dark:border-slate-800/60';
const NEEDS_TITLE = 'text-[11px] font-bold uppercase tracking-wide';
const NEEDS_OPEN = 'text-amber-700 dark:text-amber-300';
const NEEDS_WAITING = 'text-slate-500 dark:text-slate-400';
// The timeline keeps at least 12rem (6rem while the edit card is open above it).
const LOG = 'grow shrink-0 overflow-y-auto scrollbar-thin px-3 py-2';
// The edit card, above the timeline and out of its live region; it gives way and scrolls as "Needs
// you" does.
const EDITING = 'shrink min-h-[6rem] overflow-y-auto scrollbar-thin border-b border-slate-200 dark:border-slate-800/60';
const WHO = 'text-[10px] text-slate-400 dark:text-slate-500';
const USER_BUBBLE = 'max-w-[85%] rounded-2xl rounded-tr-sm px-3 py-2 text-xs bg-blue-600 text-white whitespace-pre-wrap break-words';
const AGENT_BUBBLE = 'rounded-2xl rounded-tl-sm px-3 py-2 bg-slate-100 dark:bg-slate-800/40';
const NOTICE = 'text-center text-[11px] text-slate-500 dark:text-slate-400 my-1';
const DEMO_PILL = 'ml-1 px-1 py-px rounded bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300 text-[9px] font-bold';
const DEMO_TEXT = 'mt-1 text-[11px] text-violet-700 dark:text-violet-300';
const FROM_MESSAGES = 'inline-flex items-center gap-0.5 rounded px-1 bg-slate-100 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400';
const MARKED = 'rounded-xl ring-2 ring-amber-300 dark:ring-amber-500/50';
const TEXT_BUTTON = 'text-[11px] text-slate-500 dark:text-slate-400 hover:underline';
const LIST = 'list-none pl-0 mt-1 mb-0 space-y-0.5 text-[11px] text-slate-600 dark:text-slate-400';
const SUB = 'mt-1.5 ml-3 border-l-2 border-purple-300 dark:border-purple-500/40 pl-2';
const SUB_HEAD = 'text-[11px] font-semibold text-purple-700 dark:text-purple-300';
// A run's steps, between the agent's name and its result (or, at work, its status line).
const STEPS = 'list-none pl-1 my-1 space-y-0.5 text-[11px] text-slate-600 dark:text-slate-400';
const AT_WORK = 'flex items-center gap-1.5 pl-1 mt-0.5 text-[11px] text-slate-500 dark:text-slate-400';
// A look-up that went as it should, once its run is over: lighter than what the run changed or sent.
const ROUTINE = 'text-slate-400 dark:text-slate-500';
const FOLD = 'flex items-center gap-1 max-w-full text-left text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300';
const FOLD_LIST = 'list-none pl-3 mt-0.5 space-y-0.5';
const QUIET = 'p-4 text-xs text-slate-500 dark:text-slate-400';
const ERROR = 'flex items-center justify-between gap-2 px-3 py-1.5 text-[11px] text-red-600 dark:text-red-400';
const RETRY = 'shrink-0 rounded-md px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/10';
const COMPOSER = 'flex items-end gap-2 px-3 border-t border-slate-200 dark:border-slate-800/60';
const INPUT = 'flex-1 resize-none rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-xs text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 disabled:opacity-60';
const SEND = 'rounded-md px-3 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white';
const COMPOSER_NOTE = 'px-3 pb-2 text-[11px] text-slate-500 dark:text-slate-400';

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const listOf = (value) => (Array.isArray(value) ? value.filter(isObject) : []);
const typeLabel = (types, key) => listOf(types).find((item) => item.key === key)?.label || key;
const lowerFirst = (words) => words.charAt(0).toLowerCase() + words.slice(1);
const nameOf = (names, id) => {
  const name = Object.prototype.hasOwnProperty.call(names, id) ? names[id] : null;
  return typeof name === 'string' && name.trim() ? name : 'A member';
};

const DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
function dayOf(iso) {
  if (typeof iso !== 'string' || !iso) return null;
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : DAY.format(at);
}

// What the header says under the title: the type, then the status or the schedule, then (for an
// agent that takes no runs) the schedule it keeps, or (for an active watch) when the watch ends.
function headerLine(agent, types, atWork = null) {
  // While a run is going, that is the agent's state: a one-off that runs at once is "done" as soon as
  // its run is queued, and "Done" over a chat that says "Working..." read as a contradiction.
  const parts = [typeLabel(types, agent.type), atWork || statusLabel(agent)];
  const kind = isObject(agent.trigger) ? agent.trigger.kind : null;
  if (STOPPED.has(agent.status) && kind && kind !== 'now') parts.push(describeTrigger(agent.trigger));
  if (agent.status === 'active' && kind === 'watch') {
    const until = dayOf(agent.watch_until);
    if (until) parts.push(`until ${until}`);
  }
  return parts.join(` ${DOT} `);
}

// What a halted agent says under the header.
function haltedWords(agent) {
  if (agent.status === 'paused') return agent.is_owner ? 'This agent is paused.' : `This agent is paused. ${ASK_OWNER}`;
  return `This agent is ${agent.status}.`;
}

// Why a pending card can't be decided yet, or null: a view-as session (a 403 on the server), a halted
// agent, or a card whose own run is still queued or running (cards.STILL_WORKING; both 409s).
function lockFor(card, agent, runs, viewAs) {
  if (card.status !== 'pending') return null;
  if (viewAs) return VIEW_AS_CARDS;
  if (HALTED.has(agent.status)) {
    if (agent.status === 'paused') return agent.is_owner ? RESUME_TO_DECIDE : ASK_OWNER;
    return `This agent is ${agent.status}, so its cards can't be decided.`;
  }
  const run = runs.find((item) => item.id === card.run_id);
  return run && WORKING.has(run.status) ? RUN_WORKING : null;
}

// A random id for one message, kept across its retries (client_key), as the app makes ids elsewhere.
function newKey() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    // falls through
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r % 4) + 8).toString(16);
  });
}

// The person's own entry in the members: the one with email_ok (the API gives it on the caller's
// entry alone), else the one with their id.
function selfOf(members, userId) {
  return members.find((member) => Object.prototype.hasOwnProperty.call(member, 'email_ok'))
    || members.find((member) => userId && member.user_id === userId) || null;
}

// Where the members search looks: the schema the person works in when the agent has it, else the
// chart's, else the agent's first.
function membersSchema(agent, workSchema) {
  const scope = isObject(agent.scope) ? agent.scope : {};
  const schemas = Array.isArray(scope.schemas) ? scope.schemas.filter((name) => typeof name === 'string' && name) : [];
  const work = typeof workSchema === 'string' && workSchema ? workSchema : null;
  if (work && schemas.some((name) => name.toLowerCase() === work.toLowerCase())) return work;
  const chart = isObject(scope.patient) && typeof scope.patient.schema === 'string' && scope.patient.schema ? scope.patient.schema : null;
  return chart || schemas[0] || work;
}

// A step's line: its mark, the word a screen reader says, and what a check found. A check's state
// wins over the step's own status: a step being checked is stored as failed.
function stepLine(step) {
  const check = isObject(step.check) ? step.check : null;
  const sends = SEND_ACTIONS.has(step.action);
  const went = sends ? 'went out' : 'went through';
  const source = sourceName(check?.source);
  switch (check?.state) {
    case 'happened':
      return [TICK, 'Done:', source ? `(confirmed with ${source})` : '(confirmed)'];
    case 'checking':
      return [RECHECK, 'Being checked:', `(checking whether it ${went})`];
    case 'unknown':
      // Why, in the server's own words, as the cards say it (checkWords).
      return [WARNING, 'Not confirmed:', `(couldn't confirm whether it ${went}: ${reasonWords(check.reason)})`];
    case 'not_happened': {
      const [mark, word] = step.status === 'queued' ? STEP_MARKS.get('queued') : [CROSS, 'Not done:'];
      return [mark, word, `(didn't ${sends ? 'go out' : 'go through'})`];
    }
    default: {
      const [mark, word] = STEP_MARKS.get(step.status) || [BULLET, ''];
      return [mark, word, null];
    }
  }
}

// What a step says it did. A demo run's change or send wasn't done: it is what the agent would have done.
const stepWords = (step) => (step.demo ? 'Would have: ' : '') + (step.description || String(step.action || '').replace(/_/g, ' '));

// A step that only looked something up, or kept the run's own notes, and went as it should: once its run
// is over, a few of these in a row fold into one line, as Claude Code folds its reads. A change, a send,
// a demo's "Would have", a failure, a step waiting for a decision or checked by NotesAI, what the agent
// couldn't do and its question stay in view; so does a step of no known kind.
function routine(step) {
  if (step.demo || step.status !== 'done' || isObject(step.check)) return false;
  return step.kind === 'read' || (step.kind === 'meta' && !KEEP_META.has(step.action));
}

// The steps as lines: a step done again right after itself is one line, with how many times. Steps a
// check found something about each keep their own line.
function stepLines(steps) {
  const lines = [];
  steps.forEach((step) => {
    const last = lines[lines.length - 1];
    if (last && !isObject(step.check) && !isObject(last.step.check) && last.step.status === step.status
        && !!last.step.demo === !!step.demo && stepWords(last.step) === stepWords(step)) {
      last.count += 1;
    } else {
      lines.push({ step, count: 1 });
    }
  });
  return lines;
}

// One line: its mark (a turning one for the step under way while its run is at work), the word a screen
// reader says, what it did, how many times, and what a check found.
function StepLine({ step, count = 1, live = false, quiet = false }) {
  const [mark, word, note] = step.demo ? [WOULD, '', null] : stepLine(step);
  return (
    <li className={quiet ? ROUTINE : undefined}>
      {live && step.status === 'started' && !step.demo ? <Spinner inline /> : <span aria-hidden="true">{mark}</span>}
      {word && <span className="sr-only">{word}</span>}
      {` ${stepWords(step)}`}
      {count > 1 ? ` ${TIMES}${count}` : null}
      {note ? ` ${note}` : null}
    </li>
  );
}

// Two or more lines of look-ups in a row, once their run is over: one line with how many steps they were
// and what they did, which opens to show them (and then says only how many).
function FoldedSteps({ lines }) {
  const [open, setOpen] = useState(false);
  const count = lines.reduce((sum, line) => sum + line.count, 0);
  const what = lines.map((line) => `${stepWords(line.step)}${line.count > 1 ? ` ${TIMES}${line.count}` : ''}`).join(', ');
  return (
    <li>
      <button type="button" aria-expanded={open} onClick={() => setOpen((now) => !now)} className={FOLD}>
        <span aria-hidden="true">{open ? UNFOLDED : FOLDED}</span>
        <span className="min-w-0 truncate">{open ? `${count} steps` : `${count} steps: ${what}`}</span>
      </button>
      {open && (
        <ul className={FOLD_LIST}>
          {lines.map((line) => <StepLine key={line.step.id} step={line.step} count={line.count} quiet />)}
        </ul>
      )}
    </li>
  );
}

// A run's steps once it is over, in the order they happened, above its result (or its notice): each run
// of two or more look-up lines folded into one (FoldedSteps), a look-up alone on its own lighter line,
// and the rest as they are. `steps: null` is a run whose steps weren't loaded (an older one): nothing.
function RunSteps({ run }) {
  const steps = Array.isArray(run.steps) ? run.steps.filter(isObject) : [];
  if (!steps.length) return null;
  const parts = [];
  stepLines(steps).forEach((line) => {
    const quiet = routine(line.step);
    const last = parts[parts.length - 1];
    if (quiet && last?.quiet) last.lines.push(line);
    else parts.push({ quiet, lines: [line], key: line.step.id });
  });
  return (
    <ul aria-label="What the agent did" className={STEPS}>
      {parts.map((part) => {
        if (part.quiet && part.lines.length > 1) return <FoldedSteps key={part.key} lines={part.lines} />;
        const [line] = part.lines;
        return <StepLine key={part.key} step={line.step} count={line.count} quiet={part.quiet} />;
      })}
    </ul>
  );
}

// A decided card as one line: what it was, then who decided it and when (CardItem's own words). The
// card carries no note, answer or result, by design.
function DecidedLine({ card, names }) {
  const what = card.kind === 'question' ? card.question : card.description;
  return (
    <li>
      {card.number ? `#${card.number} ` : ''}
      {what || ''}
      {` ${DOT} ${lowerFirst(decisionText(card, names))}`}
    </li>
  );
}

function Spinner({ inline = false }) {
  const size = inline ? 'inline-block w-2.5 h-2.5 align-[-1px]' : 'w-3 h-3 shrink-0';
  return (
    <svg className={`${size} animate-spin text-blue-600 dark:text-blue-400`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity=".25" />
      <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// Whether a read of the agent shows `sending` ({before, self, body}) saved: a message of the person's,
// not there before the press, with the same words.
function postedIn(data, sending) {
  if (!sending || !isObject(data)) return false;
  return listOf(data.messages).some((message) => !sending.before.has(message.id)
    && message.author_kind === 'user' && (!sending.self || message.author_id === sending.self)
    && typeof message.body === 'string' && message.body.trim() === sending.body);
}

// A message on its way (2026-10-28: "show your message in the chat the moment you send it"): where the
// person's own messages go, marked as sending, until the chat's next read shows it saved, or it is refused.
function PendingMessage({ body }) {
  return (
    <div role="article" aria-busy="true" className="flex flex-col items-end my-1.5 opacity-70">
      <div className={WHO}>{`Sending${ELLIPSIS}`}</div>
      <div className={USER_BUBBLE}>{body}</div>
    </div>
  );
}

// A run at work, at the end of the timeline (the chat reads it every 5 s while a run is going): the
// agent's name, what it is doing as a status (read out when it changes), then every step so far and each
// of its subagents at work by its type (or by the clinician's agent a task was handed to). The same step
// again is one line with how many times, the one under way turns, and nothing folds until the run is
// over. The steps aren't announced (aria-live off), or every new one would be read out over the chat.
function LiveRun({ run, runs, types }) {
  const steps = Array.isArray(run.steps) ? run.steps.filter(isObject) : [];
  const running = run.status === 'running';
  const subagents = runs.filter((other) => other.parent_run_id === run.id && other.status === 'running');
  let words = `Queued${ELLIPSIS}`;
  if (running) words = `${run.demo === true ? 'Demo run' : 'Working'}${ELLIPSIS}`;
  return (
    <div role="article" className="my-1.5">
      <div className={WHO}>
        <span aria-hidden="true">{ROBOT}</span> Agent{run.demo === true && <span className={DEMO_PILL}>Demo</span>}
      </div>
      <div role="status" className={AT_WORK}>
        <Spinner />
        {words}
        {running && !steps.length && !subagents.length && <span>{` ${DOT} starting`}</span>}
      </div>
      {(steps.length > 0 || subagents.length > 0) && (
        <ul aria-label="What the agent has done so far" aria-live="off" className={STEPS}>
          {stepLines(steps).map((line) => <StepLine key={line.step.id} step={line.step} count={line.count} live />)}
          {subagents.map((child) => (
            <li key={child.id}><span aria-hidden="true">{ELLIPSIS}</span>{` ${childLabel(child, types)} at work`}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// What a subagent is called: the clinician's agent the orchestrator handed its task to (works_as), else
// "Subagent" and its type.
const childLabel = (child, types) => child.works_as?.title || `Subagent ${DOT} ${typeLabel(types, child.type)}`;

// A subagent's heading under its parent's result, with its status. One that worked as an agent the viewer
// can open (openAgent gives a handler) names it as a button that opens that agent's chat.
function ChildHead({ child, types, openAgent }) {
  const open = child.works_as ? openAgent?.(child.works_as.id) : null;
  if (!open) return <div className={SUB_HEAD}>{`${childLabel(child, types)} ${DOT} ${child.status}`}</div>;
  return (
    <div className={SUB_HEAD}>
      <button type="button" onClick={open} className="underline underline-offset-2 hover:no-underline">
        {child.works_as.title}
      </button>
      {` ${DOT} ${child.status}`}
    </div>
  );
}

// Under a result: the run's decided cards, one line each, and its subagents with their output and their
// own decided cards.
function RunDetails({ run, runs, cards, names, types, openAgent }) {
  const decided = cards.filter((card) => card.run_id === run.id && !OPEN.has(card.status));
  const children = runs.filter((other) => other.parent_run_id === run.id);
  if (!decided.length && !children.length) return null;
  return (
    <div className="mt-1">
      {decided.length > 0 && (
        <ul className={LIST}>
          {decided.map((card) => <DecidedLine key={card.id} card={card} names={names} />)}
        </ul>
      )}
      {children.map((child) => {
        const own = cards.filter((card) => card.run_id === child.id && !OPEN.has(card.status));
        return (
          <div key={child.id} className={SUB}>
            <ChildHead child={child} types={types} openAgent={openAgent} />
            {child.output && <AgentMarkdown text={child.output} />}
            {own.length > 0 && <ul className={LIST}>{own.map((card) => <DecidedLine key={card.id} card={card} names={names} />)}</ul>}
          </div>
        );
      })}
    </div>
  );
}

// `stepsOf`: the finished run whose steps go above this message (stepAnchors), or null.
function Entry({ message, runs, cards, names, types, marked, stepsOf, onOpenSource, openAgent }) {
  // A press on one of a result's sources opens it for the result's run. The same function each render,
  // so the result's text isn't parsed again for it (AgentMarkdown is memoized).
  const runId = message.run_id || null;
  const openSource = useCallback((item) => onOpenSource?.(item, runId), [onOpenSource, runId]);
  const mark = marked ? { 'aria-current': 'true' } : {};
  const ring = marked ? ` ${MARKED}` : '';
  if (message.author_kind === 'user' && message.kind !== 'notice') {
    return (
      <div role="article" {...mark} className={`flex flex-col items-end my-1.5${ring}`}>
        <div className={WHO}>
          {nameOf(names, message.author_id)}
          {message.kind === 'instruction' ? ` ${DOT} instruction` : null}
          {message.source === 'messages' && (
            <>
              {` ${DOT} `}
              <span className={FROM_MESSAGES}><span aria-hidden="true">{BALLOON}</span>from Messages</span>
            </>
          )}
        </div>
        <div className={USER_BUBBLE}>{message.body}</div>
      </div>
    );
  }
  if (message.author_kind === 'agent' && message.kind !== 'notice') {
    const run = message.run_id ? runs.find((item) => item.id === message.run_id) : null;
    const demo = run?.demo === true;
    return (
      <div role="article" {...mark} className={`my-1.5${ring}`}>
        <div className={WHO} title={localTime(message.created_at) || undefined}>
          <span aria-hidden="true">{ROBOT}</span> Agent{demo && <span className={DEMO_PILL}>Demo</span>}
        </div>
        {stepsOf && <RunSteps run={stepsOf} />}
        <div className={AGENT_BUBBLE}>
          <AgentMarkdown text={message.body} refs={message.refs} onOpenRef={openSource} />
          <SourcesRow refs={message.refs} onOpen={openSource} />
          {run && <RunDetails run={run} runs={runs} cards={cards} names={names} types={types} openAgent={openAgent} />}
          {demo && <div className={DEMO_TEXT}>{DEMO_NOTE}</div>}
        </div>
      </div>
    );
  }
  if (stepsOf) {
    // A run that posted no result (one that failed): what it did, above the notice that says so.
    return (
      <div role="article" {...mark} className={`my-1.5${ring}`}>
        <div className={WHO}><span aria-hidden="true">{ROBOT}</span> Agent</div>
        <RunSteps run={stepsOf} />
        <div className={NOTICE}>{message.body}</div>
      </div>
    );
  }
  return <div role="article" {...mark} className={`${NOTICE}${ring}`}>{message.body}</div>;
}

// Where each finished run's steps go: {message id: run}. Above its first result, else above its last
// notice (a run that failed, or was stopped, posts one instead). A run at work has its steps at the end
// of the timeline (LiveRun), and a subagent's are its own business.
function stepAnchors(messages, runs) {
  const anchors = new Map();
  runs.forEach((run) => {
    if (run.parent_run_id || WORKING.has(run.status) || !listOf(run.steps).length) return;
    const own = messages.filter((message) => message.run_id === run.id);
    const result = own.find((message) => message.author_kind === 'agent' && message.kind !== 'notice');
    const notice = [...own].reverse().find((message) => message.kind === 'notice');
    const at = result || notice;
    if (at) anchors.set(at.id, run);
  });
  return anchors;
}

// The messages, with each run of quiet results folded into one line until it is opened (or holds the
// marked run), then each run at work (oldest first; the detail lists runs newest first).
function Timeline({ messages, runs, cards, names, types, marked, onOpenSource, openAgent, pending = null }) {
  const [opened, setOpened] = useState({});
  const groups = [];
  messages.forEach((message) => {
    const last = groups[groups.length - 1];
    if (message.quiet && last?.quiet) last.items.push(message);
    else groups.push({ quiet: !!message.quiet, items: [message], key: message.id });
  });
  const anchors = stepAnchors(messages, runs);
  const entry = (message) => (
    <Entry key={message.id} message={message} runs={runs} cards={cards} names={names} types={types} marked={marked.has(message.id)}
           stepsOf={anchors.get(message.id) || null} onOpenSource={onOpenSource} openAgent={openAgent} />
  );
  const atWork = runs.filter((run) => !run.parent_run_id && WORKING.has(run.status)).reverse();
  return (
    <>
      {groups.map((group) => {
        if (!group.quiet) return entry(group.items[0]);
        if (opened[group.key] || group.items.some((message) => marked.has(message.id))) {
          return <React.Fragment key={group.key}>{group.items.map(entry)}</React.Fragment>;
        }
        const count = group.items.length;
        return (
          <button key={group.key} type="button" onClick={() => setOpened((now) => ({ ...now, [group.key]: true }))}
                  className={`block mx-auto my-1 ${TEXT_BUTTON}`}>
            {`${count} quiet run${count === 1 ? '' : 's'}`}
          </button>
        );
      })}
      {pending && <PendingMessage body={pending.body} />}
      {atWork.map((run) => <LiveRun key={run.id} run={run} runs={runs} types={types} />)}
    </>
  );
}

// Whether the element is on screen: the tab is visible, and the element is displayed. Without an
// IntersectionObserver (tests, old browsers) it counts as displayed.
function useOnScreen(ref) {
  const [displayed, setDisplayed] = useState(() => typeof window.IntersectionObserver !== 'function');
  const [pageVisible, setPageVisible] = useState(() => !document.hidden);
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof window.IntersectionObserver !== 'function') return undefined;
    const observer = new window.IntersectionObserver((entries) => {
      const last = entries[entries.length - 1];
      if (last) setDisplayed(!!last.isIntersecting);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  useEffect(() => {
    const update = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  return displayed && pageVisible;
}

export default function AgentChat({ agentId, userId = null, types = [], version = 0, schema = null, focusRunId = null, draft = null,
  split = false, onBack, onChanged, onOpenAgent = null, openableAgents = [] }) {
  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState(null); // reading the agent failed: a sentence
  const [actionError, setActionError] = useState(null); // a change was refused: a sentence
  const [gone, setGone] = useState(false); // 404: deleted, or the person is no longer on it
  const [text, setText] = useState(draft || '');
  // A question a link brought (AgentsPage's focus.ask) fills the box once it arrives, unsent.
  useEffect(() => {
    if (draft) setText(draft);
  }, [draft]);
  const [menu, setMenu] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'delete' or 'leave', asked once more
  const [dialog, setDialog] = useState(null); // 'edit', 'members' or 'notes'
  const [working, setWorking] = useState({}); // what waits for its answer: 'send', 'run', 'delete', ...
  const [preview, setPreview] = useState(null); // the source a press opened: {item, runId, key}
  const previews = useRef(0);
  const root = useRef(null);
  const scroller = useRef(null);
  const menuRef = useRef(null);
  const moreRef = useRef(null);
  const busy = useRef(new Set());
  const started = useRef(0);
  const applied = useRef(0);
  const alive = useRef(true);
  const stick = useRef(true);
  const placed = useRef(null);
  const markedSeen = useRef(null);
  const everShown = useRef(false);
  const spellOff = useRef(false);
  const stale = useRef(false); // something changed while it was off screen: read it on return
  const readFor = useRef(null); // the agent its first read was for
  const reading = useRef(0); // reads waiting for their answer
  const goneNow = useRef(false); // the last read answered 404
  // A message whose answer never came: {before (the message ids before the press), self (the person's
  // id), body (the words), key (its client_key)}, until a read shows it posted.
  const unconfirmed = useRef(null);
  // The message being sent, shown in the chat at once: the same {before, self, body} as `unconfirmed`,
  // until a read shows it saved (load) or it is refused.
  const [pending, setPending] = useState(null);
  // In refs, so a parent passing fresh callbacks each render never re-runs an effect that calls them.
  const changed = useRef(onChanged);
  changed.current = onChanged;
  const leave = useRef(onBack);
  leave.current = onBack;

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  // The agent a handed-off task worked as, opened from its heading when it is one the viewer can open
  // (the pane's list of their agents): a click handler, or null.
  const openAgent = useCallback((id) => (
    onOpenAgent && openableAgents.includes(id) ? () => onOpenAgent(id) : null
  ), [onOpenAgent, openableAgents]);
  // The chat's one preview of a source. Each press opens a fresh one: a task's has no modal, so a press
  // can come while the one before is still being read, and that one is dropped.
  const openSource = useCallback((item, runId) => {
    previews.current += 1;
    setPreview({ item, runId, key: previews.current });
  }, []);
  const closeSource = useCallback(() => setPreview(null), []);

  // Whether `data` shows the unconfirmed message posted: one of the person's, newer than the press,
  // with the same words. Then its words go from the composer (unless the person has changed them
  // since) and so does the alert about it. No clock is compared: only ids, the author and the words.
  const confirmPosted = useCallback((data) => {
    const pending = unconfirmed.current;
    if (!pending || !isObject(data)) return false;
    const posted = listOf(data.messages).some((message) => !pending.before.has(message.id)
      && message.author_kind === 'user' && (!pending.self || message.author_id === pending.self)
      && typeof message.body === 'string' && message.body.trim() === pending.body);
    if (!posted) return false;
    unconfirmed.current = null;
    setText((now) => (now.trim() === pending.body ? '' : now));
    setActionError((now) => (now === NO_CONNECTION ? null : now));
    stick.current = true;
    return true;
  }, []);

  // Read the agent. Resolves, once the answer is in, with the detail read, or null when the read
  // failed; it never rejects, so a card can wait for it. An answer older than one already shown
  // changes nothing on screen. A read that got no answer at all says so in words, not the browser's.
  // Every read shown also settles an unconfirmed message it shows posted (confirmPosted).
  const load = useCallback(async () => {
    started.current += 1;
    const mine = started.current;
    reading.current += 1;
    let data = null;
    let failure = null;
    try {
      data = await api.getAgent(agentId);
    } catch (err) {
      failure = err || new Error('');
    } finally {
      reading.current -= 1;
    }
    if (!failure && !(isObject(data) && isObject(data.agent))) failure = new Error('');
    if (!alive.current || mine <= applied.current) return failure ? null : data;
    applied.current = mine;
    if (failure?.status === 404) {
      goneNow.current = true;
      setGone(true);
      setDetail(null);
      changed.current?.();
      return null;
    }
    if (failure) {
      setLoadError(lostAnswer(failure) ? NO_CONNECTION : agentErrorText(failure));
      return null;
    }
    goneNow.current = false;
    setDetail(data);
    // In the same update as the read that shows it saved, so it is never on screen twice.
    setPending((now) => (postedIn(data, now) ? null : now));
    setLoadError(null);
    setGone(false);
    if (confirmPosted(data)) changed.current?.();
    return data;
  }, [agentId, confirmPosted]);

  const visible = useOnScreen(root);
  const onScreen = useRef(visible);
  onScreen.current = visible;
  const hidden = detail?.history_hidden === true;
  const messages = detail && !hidden ? listOf(detail.messages) : [];
  const runs = detail && !hidden ? listOf(detail.runs) : [];
  const newest = messages.length ? messages[messages.length - 1].id : null;
  const active = runs.some((run) => WORKING.has(run.status));

  // The first read, whatever is on screen; then a read for each version (a burst of realtime events)
  // while on screen. Off screen a version only marks the chat stale, and it is read on return.
  useEffect(() => {
    if (readFor.current !== agentId || onScreen.current) {
      readFor.current = agentId;
      load();
      return;
    }
    stale.current = true;
  }, [agentId, load, version]);

  // Back on screen after a spell off it, or with changes it missed: read again at once. (Coming on
  // screen the first time, with nothing missed, isn't a reason.)
  useEffect(() => {
    if (!visible) {
      if (everShown.current) spellOff.current = true;
      return;
    }
    everShown.current = true;
    if (spellOff.current || stale.current) {
      spellOff.current = false;
      stale.current = false;
      load();
    }
  }, [visible, load]);

  // Polls skip a tick while a read is still waiting, so a slow server never gets them stacked up.
  useEffect(() => {
    if (!visible || gone) return undefined;
    const timer = setInterval(() => { if (reading.current === 0) load(); }, active ? ACTIVE_POLL_MS : IDLE_POLL_MS);
    return () => clearInterval(timer);
  }, [visible, gone, active, load]);

  // Seen: each newer message, while on screen. Then the list, so its badges drop. Never in a view-as
  // session: the server refuses it, and the person viewed is the one who hasn't seen it.
  useEffect(() => {
    if (!visible || !newest || markedSeen.current === newest || isImpersonating()) return;
    markedSeen.current = newest;
    Promise.resolve()
      .then(() => api.markAgentSeen(agentId))
      .then(() => changed.current?.(), () => { if (markedSeen.current === newest) markedSeen.current = null; });
  }, [visible, newest, agentId]);

  // The timeline opens at the newest message, or at a link's run, and follows new messages while the
  // reader is at the bottom.
  useLayoutEffect(() => {
    const box = scroller.current;
    if (!box) return;
    if (focusRunId && placed.current !== focusRunId) {
      const target = box.querySelector('[aria-current="true"]');
      if (target) {
        placed.current = focusRunId;
        stick.current = false;
        const offset = target.getBoundingClientRect().top - box.getBoundingClientRect().top;
        box.scrollTop += offset - Math.max(0, (box.clientHeight - target.offsetHeight) / 2);
        return;
      }
    }
    if (stick.current) box.scrollTop = box.scrollHeight;
  }, [detail, focusRunId]);

  const closeMenu = (focusMore) => {
    setMenu(false);
    setConfirm(null);
    if (focusMore) moreRef.current?.focus();
  };

  // The open menu: its first item takes focus, and a press anywhere else closes it.
  useEffect(() => {
    if (!menu) return undefined;
    const items = menuRef.current ? [...menuRef.current.querySelectorAll('[role="menuitem"]')] : [];
    items[0]?.focus();
    const outside = (event) => {
      if (menuRef.current?.contains(event.target) || moreRef.current?.contains(event.target)) return;
      setMenu(false);
      setConfirm(null);
    };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [menu]);

  const onMenuKey = (event) => {
    const items = [...menuRef.current.querySelectorAll('[role="menuitem"]')];
    const at = items.indexOf(document.activeElement);
    let next = null;
    if (event.key === 'ArrowDown') next = items[(at + 1) % items.length];
    else if (event.key === 'ArrowUp') next = items[at <= 0 ? items.length - 1 : at - 1];
    else if (event.key === 'Home') next = items[0];
    else if (event.key === 'End') next = items[items.length - 1];
    else if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu(true);
    } else if (event.key === 'Tab') {
      closeMenu(false);
    }
    if (next) {
      event.preventDefault();
      next.focus();
    }
  };

  // One request at a time for each thing: a second press while one waits sends nothing.
  const claim = (key) => {
    if (busy.current.has(key)) return false;
    busy.current.add(key);
    setWorking((now) => ({ ...now, [key]: true }));
    return true;
  };
  const release = (key) => {
    busy.current.delete(key);
    setWorking((now) => {
      const next = { ...now };
      delete next[key];
      return next;
    });
  };

  const refused = (err) => {
    if (err?.status === 404) {
      setGone(true);
      setDetail(null);
      changed.current?.();
      return;
    }
    setActionError(agentErrorText(err));
    // The agent may have changed under the request (paused, a member suspended): read it again.
    if (err?.status === 409 || err?.status === 403) load();
  };

  // A change to the agent; then it and the list are read again, or (leaving it) the chat closes. An
  // answer that never came (lostAnswer) may hide a change that was made, so the agent and the list
  // are read again and the chat shows where things stand:
  // - the read shows the change made (`done(read)`; for leaving, the agent gone): nothing to say,
  //   and leaving closes the chat as it would have;
  // - the read failed: its own alert (with Try again) says so, and no second one is added;
  // - else: "Couldn't reach NotesAI just now. Try again."
  const perform = async (key, request, { leaving = false, done = null } = {}) => {
    setMenu(false);
    setConfirm(null);
    if (!claim(key)) return;
    setActionError(null);
    try {
      await request();
    } catch (err) {
      release(key);
      const failure = err || new Error('');
      if (lostAnswer(failure)) {
        const now = await load();
        changed.current?.();
        if (leaving && goneNow.current) {
          leave.current?.();
          return;
        }
        if (now && !(done && done(now))) setActionError(NO_CONNECTION);
        return;
      }
      refused(failure);
      return;
    }
    release(key);
    if (leaving) {
      changed.current?.();
      leave.current?.();
      return;
    }
    await load();
    changed.current?.();
  };

  const reread = () => {
    const read = load();
    changed.current?.();
    return read;
  };

  // A message: the words stay until the server has them. Each message carries a client_key, which
  // its retries keep, so the server can post it once (Plan 4 Task 9). When the answer never came, the
  // message is unconfirmed: the agent is read again, and this read or any later one that shows it
  // posted (confirmPosted) takes the words away. A message that lands after the read (a gateway timed
  // out while the server worked on) is caught by a later read. Until then the words stay, with
  // "Couldn't reach NotesAI just now. Try again.", or with the read's own alert when it failed too.
  const send = async (event) => {
    event?.preventDefault?.();
    const body = text.trim();
    const off = !detail || hidden || HALTED.has(detail.agent.status) || isImpersonating();
    if (!body || off || !claim('send')) return;
    setActionError(null);
    // Sending the words of an unconfirmed message again is that message's retry: its key, and the
    // messages from before its first press.
    const retry = unconfirmed.current && unconfirmed.current.body === body ? unconfirmed.current : null;
    const sending = retry || {
      before: new Set(messages.map((message) => message.id)),
      self: selfOf(listOf(detail.members), userId)?.user_id || null,
      body,
      key: newKey(),
    };
    setPending(sending);
    let failure = null;
    try {
      await api.sendAgentMessage(agentId, body, sending.key);
    } catch (err) {
      failure = err || new Error('');
    }
    if (failure && !lostAnswer(failure)) {
      setPending(null);
      release('send');
      refused(failure);
      return;
    }
    if (failure) {
      unconfirmed.current = sending;
      const now = await load();
      release('send');
      // A read shown on screen settles it itself; one an even newer read overtook is checked here.
      if (!unconfirmed.current) return;
      if (confirmPosted(now)) {
        changed.current?.();
        return;
      }
      if (now) setActionError(NO_CONNECTION);
      setPending(null);        // its words are in the box, for the retry
      return;
    }
    unconfirmed.current = null;
    release('send');
    setText('');
    stick.current = true;
    await load();
    changed.current?.();
  };

  const onComposerKey = (event) => {
    // keyCode 229: a key that belongs to an IME's composition (Safari doesn't set isComposing).
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent?.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    send();
  };

  const closeEdit = () => setDialog(null);

  const backButton = (
    <button type="button" onClick={() => onBack?.()} aria-label="Back to agents" className={`${ICON_BUTTON}${split ? ' lg:hidden' : ''}`}>
      {BACK}
    </button>
  );

  if (!detail) {
    let body = <div className={QUIET}>{`Loading${ELLIPSIS}`}</div>;
    if (gone) {
      body = <div className={QUIET}>{agentErrorText('not found')}</div>;
    } else if (loadError) {
      body = (
        <div role="alert" className={`${ERROR} py-3`}>
          <span>{loadError}</span>
          <button type="button" onClick={() => load()} className={RETRY}>Try again</button>
        </div>
      );
    }
    return (
      <div ref={root} className={ROOT} style={{ minHeight: 0 }}>
        <div className={HEADER}>{backButton}</div>
        {body}
      </div>
    );
  }

  const { agent } = detail;
  const owner = agent.is_owner === true;
  const halted = HALTED.has(agent.status);
  // Entering and leaving a view-as session both load the page afresh, so this can't change under it.
  const viewAs = isImpersonating();
  const writes = !viewAs;
  const members = listOf(detail.members);
  const names = Object.fromEntries(members.filter((member) => member.user_id).map((member) => [member.user_id, member.name]));
  const me = selfOf(members, userId);
  const emailOn = me?.email_ok === true;
  const cards = hidden ? [] : listOf(detail.cards);
  const openCards = cards.filter((card) => OPEN.has(card.status));
  // "Needs you" calls for action (amber) only while a card can be decided now; cards being decided
  // or checked, or locked, are something waiting (grey).
  const decidable = openCards.some((card) => card.status === 'pending' && !lockFor(card, agent, runs, viewAs));
  const kind = isObject(agent.trigger) ? agent.trigger.kind : null;
  const composerOff = halted || hidden || viewAs;
  let composerWords = `Tell this agent something${ELLIPSIS}`;
  if (viewAs) composerWords = VIEW_AS_MESSAGES;
  else if (composerOff) composerWords = `This agent is ${agent.status}, so it can't take messages.`;

  // The messages a link's run marks: its own, else (a subagent's run) its parent's.
  let marked = new Set();
  if (focusRunId) {
    const own = messages.filter((message) => message.run_id === focusRunId);
    const parent = runs.find((run) => run.id === focusRunId)?.parent_run_id;
    const shown = own.length || !parent ? own : messages.filter((message) => message.run_id === parent);
    marked = new Set(shown.map((message) => message.id));
  }

  // What the header says while a run is going (the end of the timeline shows the rest: LiveRun).
  let atWork = null;
  if (runs.some((run) => run.status === 'running')) atWork = 'Working';
  else if (runs.some((run) => run.status === 'queued')) atWork = 'Queued';

  const editPlan = {
    plan: { title: agent.title, task: agent.instruction, type: agent.type, mode: agent.mode, trigger: agent.trigger,
            schemas: Array.isArray(agent.scope?.schemas) ? agent.scope.schemas : [], timezone: agent.timezone },
    notes: [],
    allowed_schemas: Array.isArray(detail.allowed_schemas) ? detail.allowed_schemas : [],
    policy: detail.policy, // the viewer's agent settings: the card holds Auto back when they turn it off
  };

  // How each change shows in a read of the agent, for when its own answer never came (perform's
  // `done`): Run now as a run that wasn't there before the press; a pause or a resume as a status
  // other than the one it had; the email setting as the one asked for.
  const runsBefore = new Set(runs.map((run) => run.id));
  const ranNow = (now) => listOf(now.runs).some((run) => !runsBefore.has(run.id));
  const paused = (now) => now.agent?.status === 'paused';
  const resumed = (now) => now.agent?.status !== agent.status;
  const emailSet = (now) => (selfOf(listOf(now.members), userId)?.email_ok === true) === !emailOn;
  const resume = () => perform('resume', () => api.resumeAgent(agentId), { done: resumed });

  // The menu: [key, label, what it does, whether it is asked once more (and shown in red)]. A view-as
  // session gets only what reads.
  const items = [];
  if (writes && owner && !halted) items.push(['run', 'Run now', () => perform('run', () => api.runAgentNow(agentId), { done: ranNow })]);
  // A run that changes and sends nothing, to see how the agent will do its job (views.agent_demo).
  if (writes && owner && !halted) items.push(['demo', 'Try a demo run', () => perform('demo', () => api.demoAgent(agentId), { done: ranNow })]);
  if (writes && owner && agent.status === 'active') {
    items.push(['pause', 'Pause', () => perform('pause', () => api.pauseAgent(agentId), { done: paused })]);
  }
  if (writes && owner && agent.status === 'paused') items.push(['resume', 'Resume', resume]);
  // A watch that fired, or whose period ended, is watched again from now by a resume.
  if (writes && owner && agent.status === 'done' && kind === 'watch') items.push(['resume', 'Watch again', resume]);
  if (writes && owner) items.push(['edit', 'Edit', () => setDialog('edit')]);
  items.push(['members', 'Members', () => setDialog('members')]);
  // The notes are history too: an owner whose access lapsed gets none until it is back.
  if (!hidden) items.push(['notes', 'Notes', () => setDialog('notes')]);
  items.push(['messages', <>Open in Messages <span aria-hidden="true">{OUT}</span></>,
    () => openMessages({ tab: 'chat', focus: { kind: 'agent', id: agentId } })]);
  if (writes) {
    items.push(['email', emailOn ? 'Stop emailing me' : 'Email me about runs',
      () => perform('email', () => api.setAgentEmail(agentId, !emailOn), { done: emailSet })]);
  }
  if (writes && !owner) {
    items.push(['leave', confirm === 'leave' ? 'Really leave?' : 'Leave',
      () => perform('leave', () => api.leaveAgent(agentId), { leaving: true }), true]);
  }
  if (writes && owner) {
    items.push(['delete', confirm === 'delete' ? 'Really delete?' : 'Delete',
      () => perform('delete', () => api.deleteAgent(agentId), { leaving: true }), true]);
  }
  // An item goes with the menu, so focus moves first to "...", which stays: a dialog opened from the
  // menu then gives focus back there when it closes, rather than to the page.
  const choose = (key, run, askFirst) => {
    if (askFirst && confirm !== key) {
      setConfirm(key);
      return;
    }
    moreRef.current?.focus();
    closeMenu(false);
    run();
  };

  let busyWords = null;
  if (working.delete) busyWords = `Deleting this agent${ELLIPSIS}`;
  else if (working.leave) busyWords = `Leaving this agent${ELLIPSIS}`;

  const count = members.length;
  return (
    <div ref={root} className={ROOT} style={{ minHeight: 0 }}>
      <div className={HEADER}>
        {backButton}
        <div className="flex-1 min-w-0">
          <div className={TITLE}>{agent.title}</div>
          <div className={SUBTITLE}>{headerLine(agent, types, atWork)}</div>
        </div>
        <span className={`${PILL} ${agent.mode === 'auto' ? MODE_AUTO : MODE_ASK}`}>{agent.mode === 'auto' ? 'Auto' : 'Ask'}</span>
        {/* It takes focus as it is pressed (Safari doesn't give it), for the dialog to hand it back. */}
        <button type="button" onClick={(event) => { event.currentTarget.focus(); setDialog('members'); }}
                aria-label={`${count} ${count === 1 ? 'member' : 'members'}`}
                title={members.map((member) => member.name).filter(Boolean).join(', ')} className={PEOPLE_BUTTON}>
          <span aria-hidden="true">{`${PEOPLE} ${count}`}</span>
        </button>
        <button ref={moreRef} type="button" aria-label="More" aria-haspopup="menu" aria-expanded={menu}
                onClick={() => { setMenu((now) => !now); setConfirm(null); }} className={ICON_BUTTON}>
          {MORE}
        </button>
        {menu && (
          <div ref={menuRef} role="menu" aria-label="Agent actions" onKeyDown={onMenuKey} className={MENU}>
            {items.map(([key, label, run, danger]) => (
              <button key={key} type="button" role="menuitem" tabIndex={-1} onClick={() => choose(key, run, danger)}
                      className={`${MENU_ITEM} ${danger ? DANGER_ITEM : PLAIN_ITEM}${danger && confirm === key ? ' font-semibold' : ''}`}>
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {busyWords && <div role="status" className={BUSY}>{busyWords}</div>}

      {halted && (
        <div className={BANNER}>
          <span>{haltedWords(agent)}</span>
          {writes && owner && agent.status === 'paused' && (
            <button type="button" disabled={!!working.resume} onClick={resume}
                    className={RESUME}>
              Resume
            </button>
          )}
        </div>
      )}

      {openCards.length > 0 && (
        <section aria-label="Needs you" className={NEEDS}>
          <div className={`${NEEDS_TITLE} ${decidable ? NEEDS_OPEN : NEEDS_WAITING}`}>
            {halted ? `Needs you ${DOT} ${agent.status}` : 'Needs you'}
          </div>
          {openCards.map((card) => (
            <CardItem key={card.id} card={card} names={names} locked={lockFor(card, agent, runs, viewAs)}
                      onDecided={() => { load(); changed.current?.(); }} onRefresh={reread} />
          ))}
        </section>
      )}

      {/* The edit card sits outside the timeline: it is a live region (a log), so what is put in it is
          read out. */}
      {dialog === 'edit' && (
        <div role="region" aria-label="Edit this agent" className={EDITING}>
          <PlanCard key={agent.id} plan={editPlan} types={types} agentId={agent.id} patient={agent.scope?.patient || null}
                    onCancel={closeEdit} onDeployed={() => { closeEdit(); load(); changed.current?.(); }} />
        </div>
      )}

      <div ref={scroller} role="log" aria-label="Agent chat" className={`${LOG} ${dialog === 'edit' ? 'basis-24' : 'basis-48'}`}
           style={{ minHeight: 0 }}
           onScroll={(event) => {
             const box = event.currentTarget;
             stick.current = box.scrollHeight - box.scrollTop - box.clientHeight < STICK_PX;
           }}>
        {!hidden && detail.paging?.older_messages === true && <div className={NOTICE}>{OLDER_HIDDEN}</div>}
        {hidden ? (
          <div className={QUIET}>{HISTORY_HIDDEN}</div>
        ) : (
          <Timeline messages={messages} runs={runs} cards={cards} names={names} types={types} marked={marked}
                    onOpenSource={openSource} openAgent={openAgent} pending={pending} />
        )}
      </div>

      {actionError && <div role="alert" className={ERROR}><span>{actionError}</span></div>}
      {loadError && (
        <div role="alert" className={ERROR}>
          <span>{loadError}</span>
          <button type="button" onClick={() => load()} className={RETRY}>Try again</button>
        </div>
      )}

      <form onSubmit={send} className={`${COMPOSER} ${composerOff ? 'py-2' : 'pt-2 pb-1'}`}>
        <textarea aria-label="Tell this agent something" rows={2} maxLength={MAX_MESSAGE} disabled={composerOff}
                  readOnly={!!working.send} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onComposerKey}
                  placeholder={composerWords} className={INPUT} />
        <button type="submit" disabled={composerOff || !!working.send || !text.trim()} className={SEND}>
          {working.send ? `Sending${ELLIPSIS}` : 'Send'}
        </button>
      </form>
      {!composerOff && <div className={COMPOSER_NOTE}>{`Also posts to ${ROBOT} ${agent.title} in Messages.`}</div>}

      {dialog === 'members' && (
        <MembersDialog agentId={agentId} isOwner={owner} schema={membersSchema(agent, schema)} title={agent.title || ''}
                       readOnly={viewAs} onClose={() => setDialog(null)} onChanged={() => { load(); changed.current?.(); }}
                       onLeft={() => { setDialog(null); changed.current?.(); leave.current?.(); }} />
      )}
      {dialog === 'notes' && <NotesDialog agentId={agentId} readOnly={viewAs} onClose={() => setDialog(null)} />}
      {preview && (
        <SourcePreview key={preview.key} agentId={agentId} runId={preview.runId} item={preview.item} onClose={closeSource} />
      )}
    </div>
  );
}
